import _ from "lodash";
import React, { useState, useEffect } from "react";

// design system
import { Tooltip } from "@toddle-edu/ds-web";
import { PencilLineOutlined } from "@toddle-edu/ds-icons";

// components
import I18nHOC from "UIComponents/I18nHOC";
import ContextSelectionModal from "./components/ContextSelectionModal";
import { AttachmentList } from "Attachment";
import TheatreModeV2 from "UIComponents/TheatreModeV2";

// third party
import classNames from "classnames";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";

// modules
import { getCurrentCurriculumProgramType } from "modules/CommonSelectors";
import { getAssessmentObj } from "ClassRoom/modules/ClassRoomModule";
import { deleteAIConversationAttachment } from "../../modules/AIContextBarModule";
import { openLinkInNewTab } from "modules/Services";

// utils
import { getFieldValue } from "Assessments/modules/Utils";
import { extractContentFromHTML, isIdEqual, getStyleStrippedText } from "Utils";
import { getEntityAndFieldType, cleanLabel } from "../../modules/Utils";

// constants
import {
  UNIT,
  ASSESSMENT,
  CURRICULUM_TYPE_MYP,
} from "Constants/stringConstants";
import { MODAL_STATE, TEMP_ID } from "../../modules/Constants";

// scss
import classes from "./ContextSelection.scss";

const styles = {
  overlayInnerStyle: {
    maxWidth: "344px",
    maxHeight: "500px",
    overflowY: "auto",
    whiteSpace: "pre-wrap",
  },
  attachmentContainerStyle: {
    gap: "8px",
    gridTemplateColumns: "1fr",
  },
  iconContainerStyle: {
    margin: "0px",
  },
};

const ContextSelection = props => {
  const {
    t,
    contextFields,
    assessmentTemplate,
    unitPlanTemplate,
    assessmentDetails,
    unitPlanDetails,
    curriculumType,
    assessmentType,
    getAssessmentObj,
    selectedField,
    setSelectedField,
    excludedFields,
    setExcludedFields,
    showContextList,
    deleteAIConversationAttachment,
    aiConversationId,
    isWritingMessage,
    containerRef,
    onAttachmentsUpdate,
    openLinkInNewTab,
  } = props;

  const [mediaViewerData, setMediaViewerModalState] = useState(null);

  useEffect(() => {
    if (curriculumType === CURRICULUM_TYPE_MYP) {
      _.forEach(contextFields, contextField => {
        const { field, isExcluded } = contextField || {};

        if (!_.isUndefined(isExcluded)) {
          if (isExcluded) {
            if (!_.includes(excludedFields, field)) {
              setExcludedFields(prevState => [...prevState, field]);
            }
          } else {
            if (_.includes(excludedFields, field)) {
              setExcludedFields(prevState =>
                _.filter(prevState, excludedField => excludedField !== field)
              );
            }
          }
        }
      });
    }
  }, [curriculumType, contextFields]);

  // As we don't unmount this component and just hide it, we need to scroll to top when a context window is opened
  useEffect(() => {
    if (showContextList && containerRef?.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [showContextList]);

  // variables
  const typeObj = getAssessmentObj({
    curriculumType,
    assessmentValue: assessmentType,
    contentType: ASSESSMENT,
  });

  const { typeLabel, typeLabel2 } = typeObj || {};
  const assessmentTypeLabel = typeLabel || typeLabel2;

  // functions
  const onEditField = params => {
    const { step, config } = params;

    const { field, type } = getEntityAndFieldType({
      ...params,
      assessmentDetails,
      unitPlanDetails,
    });

    const isUnitDependent = type === UNIT;

    const stepList = isUnitDependent
      ? unitPlanTemplate?.step_list
      : assessmentTemplate?.step_list;

    // if step is defined for any pre-requisite then render all the fields of step
    if (stepList?.[step]) {
      const stepFields = _.get(stepList, `${step}.fields`, []);
      setSelectedField({
        fields: _.map(stepFields, field => ({ field })),
        step,
        isUnitDependent,
        modalState: MODAL_STATE.OPEN,
      });
    } else {
      setSelectedField({
        fields: [{ field, type, config }],
        step,
        isUnitDependent,
        modalState: MODAL_STATE.OPEN,
      });
    }
  };

  const onClose = () => {
    setSelectedField({ modalState: MODAL_STATE.CLOSED });
  };

  const onDeleteAttachment = async attachment => {
    const attachmentId = _.get(attachment, "attachmentId");
    if (attachmentId && !_.includes(attachmentId, TEMP_ID)) {
      await deleteAIConversationAttachment({
        input: { attachmentIds: [attachmentId] },
        aiConversationId,
      });
      onAttachmentsUpdate({ type: "DELETE", id: attachmentId });
    }
  };

  const onClickAttachmentCard = (attachments, selectedAttachment) => {
    // skip theatre mode for link
    if (selectedAttachment?.type === "LINK") {
      openLinkInNewTab({ url: selectedAttachment?.url });
      return;
    }

    const activeAttachmentIndex = _.findIndex(attachments, attachment =>
      isIdEqual(attachment.id, selectedAttachment.id)
    );

    setMediaViewerModalState({
      attachments,
      activeAttachmentIndex,
    });
  };

  const renderMediaViewer = () => {
    if (_.isEmpty(mediaViewerData)) {
      return null;
    }

    const { attachments, activeAttachmentIndex } = mediaViewerData;

    const closePreviewModal = () => {
      setMediaViewerModalState(null);
    };

    return (
      <TheatreModeV2
        mediaAttachments={{ attachments }}
        activeAttachmentIndex={activeAttachmentIndex}
        toggleTheatreMode={closePreviewModal}
      />
    );
  };

  // classes
  const labelClass = classNames("text-body-2", classes.label);

  return (
    <>
      <div className={classes.container} ref={containerRef}>
        {_.map(contextFields, contextField => {
          const {
            field,
            type,
            label,
            value,
            tooltip,
            isRequired,
            isDisabled,
            disabledTooltip,
          } = contextField || {};

          const isAttachmentField = field === "attachments";
          const key = `${field}-${type}`;

          const parsedValue = _.trim(
            extractContentFromHTML({ htmlString: value })
          );

          const sanitizedValue = _.isString(value)
            ? getStyleStrippedText({
                text: value,
                type: "SANITIZE_DANGER_HTML_PARAGRAPH",
              })
            : "";

          const __html = sanitizedValue ? sanitizedValue : t("common:select");

          const parsedTooltip = _.trim(
            extractContentFromHTML({ htmlString: tooltip })
          );

          const parsedLabel = cleanLabel(label);

          const onValueClick = () => {
            if (!isDisabled) onEditField(contextField);
          };

          const handleKeyDown = e => {
            if (e.key === "Enter" || e.key === " ") {
              onValueClick();
            }
          };

          // Determine which tooltip to show for the value
          let valueTooltip;
          if (isDisabled && disabledTooltip) {
            valueTooltip = disabledTooltip;
          } else if (!_.isEmpty(parsedTooltip)) {
            valueTooltip = parsedTooltip;
          } else {
            valueTooltip = parsedValue;
          }

          // classes
          const valueClass = classNames("text-body-2", classes.value, {
            [classes.emptyValue]: _.isEmpty(value),
          });

          const valueContainerClass = classNames(classes.valueContainer, {
            [classes.disabledValueContainer]: isDisabled,
          });

          const contextContainerClass = classNames(classes.contextContainer, {
            [classes.attachmentContextContainer]: isAttachmentField,
          });

          const labelContainerClass = classNames(classes.flex, {
            [classes.attachmentLabelContainer]: isAttachmentField,
          });

          return (
            <div key={key} className={contextContainerClass}>
              {/* Label */}
              <Tooltip
                visible={_.size(parsedLabel) > 25}
                tooltip={parsedLabel}
                placement={"top"}
                overlayInnerStyle={styles.overlayInnerStyle}
                showArrow={true}
              >
                <div className={labelContainerClass}>
                  <div className={labelClass}>{parsedLabel}</div>
                  {isRequired ? (
                    <span className={classes.asterisk}>{"*"}</span>
                  ) : null}
                </div>
              </Tooltip>

              {/* Value */}
              {isAttachmentField ? (
                <div className={classes.attachmentValueContainer}>
                  <AttachmentList
                    value={value}
                    parentId={aiConversationId}
                    parentType={"AI_CONVERSATION"}
                    mode={"view"}
                    mediaCardSize={"small"}
                    cardType={"design-media-card"}
                    showDeleteForSelfInViewMode={true}
                    showRenameAttachmentOption={false}
                    showAttachmentAsPost={false}
                    onDeleteAttachmentCallback={onDeleteAttachment}
                    disableActions={isWritingMessage}
                    attachmentContainerStyle={styles.attachmentContainerStyle}
                    iconContainerStyle={styles.iconContainerStyle}
                    onClickAttachmentCard={selectedAttachment =>
                      onClickAttachmentCard(value, selectedAttachment)
                    }
                    isTempAttachmentDeleteDisabled={true}
                  />
                </div>
              ) : (
                <div
                  className={valueContainerClass}
                  onClick={onValueClick}
                  onKeyDown={handleKeyDown}
                  tabIndex={0}
                  role={"button"}
                >
                  <Tooltip
                    tooltip={valueTooltip}
                    placement={"topLeft"}
                    visible={
                      isDisabled
                        ? !_.isEmpty(disabledTooltip)
                        : !_.isEmpty(parsedTooltip) || !_.isEmpty(parsedValue)
                    }
                    overlayInnerStyle={styles.overlayInnerStyle}
                    showArrow={true}
                  >
                    <div
                      className={valueClass}
                      dangerouslySetInnerHTML={{ __html }}
                    />
                  </Tooltip>
                  {!isDisabled ? (
                    <PencilLineOutlined size={"xxx-small"} variant={"subtle"} />
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!_.isEmpty(selectedField?.fields) ? (
        <ContextSelectionModal
          {...props}
          {...(selectedField || {})}
          onClose={onClose}
          assessmentTypeLabel={assessmentTypeLabel}
        />
      ) : null}

      {renderMediaViewer()}
    </>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { assessmentDetails: { allFields } = {} } = ownProps;
  const curriculumType = getCurrentCurriculumProgramType(state);

  const assessmentType = getFieldValue({
    fields: allFields,
    uid: "assessmentType",
  });

  return { curriculumType, assessmentType };
};

const mapActionCreators = {
  getAssessmentObj,
  deleteAIConversationAttachment,
  openLinkInNewTab,
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, mapActionCreators)
)(ContextSelection);
